import {
  ApolloClient,
  ApolloLink,
  ApolloProvider,
  InMemoryCache,
  createHttpLink,
} from '@apollo/client';
import { setContext } from '@apollo/client/link/context';
import { onError } from '@apollo/client/link/error';
import { removeTypenameFromVariables } from '@apollo/client/link/remove-typename';
import { RetryLink } from '@apollo/client/link/retry';
import { GraphQLWsLink } from '@apollo/client/link/subscriptions';
import { ServerError } from '@apollo/client/link/utils';
import { getOperationName } from '@apollo/client/utilities';
import { createStandaloneToast } from '@chakra-ui/react';
import { MultiAPILink } from '@habx/apollo-multi-endpoint-link';
import { Severity } from '@sentry/react';
import { OperationDefinitionNode } from 'graphql';
import { createClient } from 'graphql-ws';
import React, { ReactNode, useEffect, useState } from 'react';

import customTheme from '../chakra';
import config, { configs } from '../config';
import { PAYOFF_INSTRUCTIONS } from '../constants/urls';
import { totalCompletedDeals } from '../gql/dealGql';
import {
  CreateAffiliateTemporaryInfoDocument,
  CreateManualEntryTemporaryInfoDocument,
  CreateTemporaryInfoDocument,
  ErrorCodeEnum,
} from '../gql/generated/graphql';
import { uploadDocument, uploadDriversLicense } from '../gql/tatertitleGql';
import { CookieKeys, getCookie, useCookie } from '../hooks/useCookie';
import hotjar, { HotjarEvents } from '../services/analytics/hotjar';
import {
  RudderEvent,
  rudderanalytics,
  waitForAnonymousId,
} from '../services/analytics/rudderstack';
import { handleErrorWithSentry } from '../services/sentry';
import { resetAuth } from '../utils/auth';
import { createErrorToast } from '../utils/toast';

const HIDDEN_ERROR_MESSAGES = ['SSN does not match', 'Socket closed', 'Max attempt limit reached.'];

const GraphQLEndpoints = {
  LE: 'le',
  LE_PROD: 'le_prod',
  PRS: 'prs',
} as const;

const allowedErrorPathsAndOperations: (string | number)[] = [
  ...[totalCompletedDeals, uploadDocument, uploadDriversLicense].map(
    (operation) =>
      (operation?.definitions[0] as OperationDefinitionNode)?.name?.value as string | number,
  ),
  ...[PAYOFF_INSTRUCTIONS],
];

const allowErrors = (path: readonly (string | number)[] | undefined): boolean | undefined =>
  path?.some((p) => allowedErrorPathsAndOperations.includes(p));

const cache = new InMemoryCache({
  typePolicies: {
    Query: {
      fields: {},
    },
    deals: {
      keyFields: ['id', 'isCobuyer'],
    },
  },
});

const { toast } = createStandaloneToast({ theme: customTheme });

const errorLink = onError(({ graphQLErrors, networkError, operation }) => {
  const operationName = operation?.operationName;
  const { pathname } = window.location;

  const { isErrorHandled } = operation.getContext();
  if (graphQLErrors) {
    graphQLErrors.forEach(({ message, path, extensions }) => {
      // Handled errors are expected validations - log as warnings, not errors.
      handleErrorWithSentry(
        `[GraphQL error]: Message: ${message}, Operation: ${operationName}, Path: ${path}, Pathname: ${pathname}, Extensions: ${JSON.stringify(
          extensions,
        )}${isErrorHandled ? ' (handled)' : ''}`,
        {},
        isErrorHandled ? Severity.Warning : Severity.Error,
      );
      rudderanalytics.track(RudderEvent.Error, {
        error_message: message,
        pathname,
        operationName,
        isErrorHandled,
      });
      hotjar.track(HotjarEvents.ApolloError);

      if (extensions && extensions.code === ErrorCodeEnum.Unauthenticated) {
        resetAuth({ removeGuid: true, removeAccessToken: true });
      } else if (
        !allowErrors(path) &&
        !isErrorHandled &&
        !HIDDEN_ERROR_MESSAGES.some((hiddenMessage) => message.includes(hiddenMessage))
      ) {
        toast(createErrorToast({ errorMessage: message, logError: false }));
      }
    });
  }

  if (networkError) {
    const { message, statusCode, response } = networkError as ServerError;

    rudderanalytics.track(RudderEvent.Error, {
      error_message: message,
      pathname,
      operationName,
      statusCode,
      responseText: response?.statusText,
      isErrorHandled,
    });
    hotjar.track(HotjarEvents.NetworkError);

    if ((networkError as ServerError).statusCode === 401) {
      resetAuth({ removeGuid: true, removeAccessToken: true });
    } else if (
      !allowErrors([window.location.pathname]) &&
      !isErrorHandled &&
      !HIDDEN_ERROR_MESSAGES.includes(message)
    ) {
      toast(createErrorToast({ errorMessage: message, logError: false }));
    }
  }
});

const retryLink = new RetryLink({
  attempts: {
    max: 3,
    retryIf: (error, operation) => {
      const shouldRetry =
        !!error &&
        (error.statusCode === undefined || // Pure network errors
          error.statusCode === 408 || // Request Timeout
          error.statusCode === 502 || // Bad Gateway
          error.statusCode === 503 || // Service Unavailable
          error.statusCode === 504); // Gateway Timeout

      if (shouldRetry) {
        rudderanalytics.track(RudderEvent.ErrorRetry, {
          error_message: error.message,
          operation_name: operation.operationName,
          status_code: error.statusCode,
        });
      }

      return shouldRetry;
    },
  },
  delay: {
    initial: 1000,
    max: 5000,
    jitter: true,
  },
});

const getAuthHeader = () => {
  const accessToken = getCookie<string>(CookieKeys.ACCESS_TOKEN);
  const guid = getCookie<string>(CookieKeys.GUID_KEY);
  // Non-critical attribution header; fail open so a RudderStack SDK error
  // (e.g. getAnonymousId before load() when no write key is configured)
  // can never break GraphQL requests.
  let anonymousId: string | undefined;
  try {
    anonymousId = rudderanalytics.getAnonymousId();
  } catch {
    anonymousId = undefined;
  }
  return {
    ...(accessToken && {
      authorization: `Bearer ${accessToken}`,
    }),
    ...(guid && {
      guid,
    }),
    ...(anonymousId && {
      'x-rudderstack-anonymous-id': anonymousId,
    }),
  };
};

export const ResetWsContext = React.createContext<{ resetWebSocket: () => void }>({
  resetWebSocket: () => undefined,
});

const apolloClient = new ApolloClient({
  cache,
  connectToDevTools: true,
});

const removeTypenameLink = removeTypenameFromVariables();

// leaseend-api saves the x-rudderstack-anonymous-id header on the temp infos these create
const ANONYMOUS_ID_OPERATIONS = [
  CreateTemporaryInfoDocument,
  CreateManualEntryTemporaryInfoDocument,
  CreateAffiliateTemporaryInfoDocument,
].map(getOperationName);

const anonymousIdLink = ApolloLink.split(
  ({ operationName }) => ANONYMOUS_ID_OPERATIONS.includes(operationName),
  setContext(() => waitForAnonymousId().then(() => ({}))),
);

const AuthorizedApolloProvider = ({ children }: { children: ReactNode }) => {
  const [guid] = useCookie<string>(CookieKeys.GUID_KEY);
  const [accessToken] = useCookie<string>(CookieKeys.ACCESS_TOKEN);
  const [resetWebSocket, setResetWebSocket] = useState<() => void>();

  useEffect(() => {
    const ws = new GraphQLWsLink(
      createClient({
        url: `${config.urls.wsRoot}/graphql`,
        connectionParams: async () => {
          return getAuthHeader();
        },
      }),
    );

    const multiApiLink = new MultiAPILink({
      endpoints: {
        [GraphQLEndpoints.LE]: config.urls.apiRoot,
        [GraphQLEndpoints.LE_PROD]: configs.production.urls.apiRoot,
        [GraphQLEndpoints.PRS]: config.urls.prsApiRoot,
      },
      createWsLink: () => ws,
      defaultEndpoint: GraphQLEndpoints.LE,
      getContext: (_, getCurrentContext) => ({
        headers: { ...getCurrentContext().headers, ...getAuthHeader() },
      }),
      createHttpLink: () =>
        createHttpLink({
          fetch: (uri, options) => {
            let operationName: string | null = null;
            if (typeof options?.body === 'string') {
              try {
                operationName = JSON.parse(options.body)?.operationName ?? null;
              } catch {
                // body wasn't JSON; use plain uri
              }
            }
            return fetch(operationName != null ? `${uri}?op=____${operationName}` : uri, options);
          },
        }),
    });

    apolloClient.setLink(
      ApolloLink.from([errorLink, retryLink, removeTypenameLink, anonymousIdLink, multiApiLink]),
    );
    setResetWebSocket(() => () => ws.client.terminate());
  }, [guid, accessToken]);

  return apolloClient && resetWebSocket ? (
    <ApolloProvider client={apolloClient}>
      <ResetWsContext.Provider value={{ resetWebSocket }}>{children}</ResetWsContext.Provider>
    </ApolloProvider>
  ) : (
    <></>
  );
};

export default AuthorizedApolloProvider;
export { apolloClient };
